/**
 * 吸管（M3-04 / DESIGN.md §7.4 的 I 键）。
 *
 * 取的是**合成结果**的颜色（PS 的「所有图层」取样模式）——用户看到什么
 * 就取到什么，混合模式与调整图层的效果都包含在内。
 */
import { compositeRegion } from '../../app/composite';
import type { AppStore, RgbColor } from '../../app/store';

/**
 * 取文档坐标 (x, y) 处的合成色。越界或无文档返回 null。
 * 全透明处返回黑——与 PS 在空画布上取样的行为一致。
 */
export function sampleDocumentColor(store: AppStore, x: number, y: number): RgbColor | null {
  const document = store.document;
  if (document === null) return null;
  const px = Math.floor(x);
  const py = Math.floor(y);
  if (px < 0 || py < 0 || px >= document.size.width || py >= document.size.height) return null;

  const out = new Uint8Array(4);
  compositeRegion(document, store.tiles, { x: px, y: py, width: 1, height: 1 }, out);
  return { r: out[0]!, g: out[1]!, b: out[2]! };
}

/**
 * 吸管的实时光标（PS 惯例：鼠标变成当前悬停像素颜色的小圆环，
 * 而不是通用的十字线——不用等点下去才知道会取到什么颜色）。
 * `color` 为 null（文档外/尚无采样）时画空心环，不误导用户"这里也是这个色"。
 * 返回可以直接赋给 CSS `cursor` 的值；圆心（数据 URI 里的 11 11）就是热点，
 * 即实际取样的那个像素，必须与 SVG 里圆心坐标一致。
 */
export function eyedropperCursor(color: RgbColor | null): string {
  const fill = color === null ? 'none' : `rgb(${color.r},${color.g},${color.b})`;
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22">' +
    `<circle cx="11" cy="11" r="7.5" fill="${fill}" stroke="#000" stroke-width="2"/>` +
    '<circle cx="11" cy="11" r="7.5" fill="none" stroke="#fff" stroke-width="1"/>' +
    '</svg>';
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 11 11, crosshair`;
}

/** 吸管的指针处理：down 即取色进前景。返回是否消费了事件。 */
export function handleEyedropper(
  store: AppStore,
  activeToolId: string,
  phase: 'down' | 'move' | 'up' | 'double',
  point: { x: number; y: number },
): boolean {
  if (activeToolId !== 'eyedropper') return false;
  if (phase !== 'down') return true; // 独占指针但只在按下时取样
  const color = sampleDocumentColor(store, point.x, point.y);
  if (color !== null) store.setForeground(color);
  return true;
}
